import { useCallback, useContext, useEffect, useRef } from "react";
import { LoggerContext } from "./logger-provider";
import { useLocation, useNavigationType } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { sendLogs } from "./logs-service";
import type { BaseLogEvent } from "./logger-core";
import { type LogEventType } from "./logger-core";
import {
  getScreenMetadataFromPath,
  loggerContextManager,
} from "./smart-context-provider";
import {
  LogCategories,
  LogEventTypeEnum as LogEventTypeEnum,
  LogSubCategories,
  LogTags,
} from "./logger-types";
import { getUserInteractionTitle } from "./logger-utils";
import { capitalizeInitialLetter } from "@/lib/string-util";
import { getItem } from "@/lib/storage-utils";
import { STORAGE, STORAGE_KEY } from "@/constants";
import { AuthMode } from "@/context/auth-provider";

const lastActionLogAtByKey = new Map<string, number>();
let lastRoutePageViewKey: string | null = null;

export function useLogger() {
  const context = useContext(LoggerContext);

  if (!context) throw new Error("useLogger must be within LoggerProvider");

  return context?.logger;
}

/**
 * Hook to track route based events
 * For eg: /dashboard, /care-history
 * Call this once in your layout or page wrapper
 */
export function useRouteLogger() {
  const logger = useLogger();
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevRouteRef = useRef<string | null>(null);
  const enterAtRef = useRef<number>(Date.now());

  useEffect(() => {
    const screenRouteMetaData = getScreenMetadataFromPath(location.pathname);
    if (!screenRouteMetaData) return;
    const dialogCtx = loggerContextManager.getCurrentDialogContext();
    const contextToSet = { ...screenRouteMetaData };
    if (dialogCtx?.subCategory) {
      contextToSet.subCategory = dialogCtx.subCategory;
    }
    loggerContextManager.setGlobalContext(contextToSet);
  }, [location.pathname]);

  useEffect(() => {
    const current = location.pathname + location.search;
    const currentMeta = getScreenMetadataFromPath(location.pathname);
    const now = Date.now();

    if (prevRouteRef.current && prevRouteRef.current !== current) {
      const prevPath = prevRouteRef.current.split("?")[0].split("#")[0];
      const prevMeta = getScreenMetadataFromPath(prevPath);
      const prevScreen = prevMeta?.screenName ?? "UNKNOWN";
      const rawTitle = `${prevScreen.split("_").join(" ").toLowerCase()} screen closed`;
      const logTitle = capitalizeInitialLetter(rawTitle);

      logger.log({
        category: prevMeta?.category ?? LogCategories.NONE,
        context: {},
        duration: now - enterAtRef.current,
        tag: prevMeta?.tag ?? LogTags.NONE,
        title: logTitle,
        subCategory: prevMeta?.subCategory ?? LogSubCategories.NONE,
        type: LogEventTypeEnum.PAGE_VIEW,
        mode:
          getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION)?.mode ||
          AuthMode.MEMBER,
        module:
          prevMeta?.subCategory || prevMeta?.category || LogCategories.NONE,
      });
    }

    if (prevRouteRef.current !== current && currentMeta) {
      // Dedupe PAGE_VIEW per route key (pathname+search) within a tab/session
      const onGuestMode =
        currentMeta?.screenName === "GUEST_LOGIN" ? true : false;
      if (lastRoutePageViewKey === current) {
        prevRouteRef.current = current;
        enterAtRef.current = now;
        return;
      }

      const currentScreen = currentMeta.screenName ?? "UNKNOWN";
      const rawTitle = `${currentScreen.split("_").join(" ").toLowerCase()} screen opened`;
      const logTitle = capitalizeInitialLetter(rawTitle);

      logger.log({
        category: currentMeta.category,
        context: {},
        tag: currentMeta.tag,
        title: logTitle,
        subCategory: currentMeta.subCategory,
        type: LogEventTypeEnum.PAGE_VIEW,
        mode: onGuestMode
          ? AuthMode.GUEST
          : getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION)?.mode ||
            AuthMode.MEMBER,
        module:
          currentMeta.subCategory || currentMeta.category || LogCategories.NONE,
      });
      prevRouteRef.current = current;
      enterAtRef.current = now;
      lastRoutePageViewKey = current;
    }
  }, [logger, location.pathname, location.search, navigationType]);
}

export function useActionLogger() {
  const logger = useLogger();
  const location = useLocation();

  const logAction = useCallback(
    (
      actionName: string,
      logContext: Record<string, unknown>,
      options?: {
        debounceMs?: number;
        suppressDuplicate?: boolean;
        /** Override category (e.g. from Button logCategory prop) */
        tag?: LogTags;
        category?: LogCategories;
        subCategory?: LogSubCategories;
        includeFormContext?: boolean;
        /** Use actionName as title without "x button clicked from y dialog" transform (e.g. dialog opened/closed) */
        useTitleAsIs?: boolean;
        type?: LogEventType;
      },
    ) => {
      try {
        // Validate actionName
        if (!actionName?.trim()) return;

        // Use current location for screen context (getActiveContext can be stale due to render order).
        // Merge route metadata from location with dialog context. Active context (incl. dialog) overrides route
        // so that overlay/dialog subCategory (e.g. GROUP_INFO_MODAL) wins over route's subCategory.
        const routeMeta = getScreenMetadataFromPath(location.pathname);
        const activeContext = loggerContextManager.getActiveContext();
        const currentContext = routeMeta
          ? { ...routeMeta, ...activeContext }
          : activeContext;

        // Edge case: Prevent duplicate rapid clicks (debounce)
        const lastLogKey = `lastLog_${actionName}`;
        const lastLogTime = lastActionLogAtByKey.get(lastLogKey);
        const debounceMs = options?.debounceMs ?? 0;
        const rawTitle = `${actionName.split("-").join(" ")}`;
        const titleValue = capitalizeInitialLetter(rawTitle);

        if (lastLogTime && Date.now() - lastLogTime < debounceMs) {
          if (options?.suppressDuplicate) return;
        }

        lastActionLogAtByKey.set(lastLogKey, Date.now());

        const formContext = options?.includeFormContext
          ? loggerContextManager.getFormContext()
          : {};
        if (options?.includeFormContext) {
          loggerContextManager.clearFormContext();
        }

        const hasFormContext =
          options?.includeFormContext &&
          formContext &&
          Object.keys(formContext).length > 0;

        let eventContext: Record<string, unknown> = {
          ...logContext,
        };

        if (hasFormContext) {
          eventContext = { ...eventContext, ...formContext };
        }

        const resolvedSubCategory =
          options?.subCategory ??
          currentContext?.subCategory ??
          LogSubCategories.NONE;
        const resolvedCategory =
          options?.category ?? currentContext?.category ?? LogCategories.NONE;
        const logEvent: BaseLogEvent = {
          category: resolvedCategory,
          context: eventContext,
          tag: options?.tag ?? currentContext?.tag ?? LogTags.NONE,
          title: titleValue,
          subCategory: resolvedSubCategory,
          type: options?.type
            ? options?.type
            : LogEventTypeEnum.USER_INTERACTION,
          mode:
            getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION)?.mode ||
            AuthMode.MEMBER,
          module: resolvedSubCategory || resolvedCategory || LogCategories.NONE,
        };

        logger.log(logEvent);
      } catch (error) {
        console.error(`Error logging user action ${actionName}:`, error);
      }
    },
    [logger, location.pathname],
  );

  return { logAction };
}

type PanelLogOptions = {
  tag?: LogTags;
  category?: LogCategories;
  subCategory?: LogSubCategories;
  /**
   * Panels are overlays within a route, not route navigations.
   * Default remains "USER_INTERACTION". Keep route-only types for `useRouteLogger`.
   */
  type?: LogEventType;
  duration?: number;
};

/**
 * Panel logger: standardizes logging for "open/close/action" style panels.
 * Uses the same context-merge strategy as action logging (route meta + active overlay context).
 *
 * Titles:
 * - For consistency with existing app usage, callers should pass a `data-testid` constant
 *   (from `client/src/constants/test_id.ts`) as the base title.
 * - The title is used as-is (no suffix); event kind goes into context.panelEvent.
 */
export function usePanelLogger() {
  const logger = useLogger();
  const location = useLocation();

  const buildBaseContext = useCallback(() => {
    const routeMeta = getScreenMetadataFromPath(location.pathname);
    const activeContext = loggerContextManager.getActiveContext();
    return routeMeta ? { ...routeMeta, ...activeContext } : activeContext;
  }, [location.pathname]);

  const logPanelEvent = useCallback(
    (
      panelTitleBase: string,
      panelContext: Record<string, unknown>,
      options?: PanelLogOptions,
    ) => {
      if (!panelTitleBase?.trim()) return;

      const currentContext = buildBaseContext();
      const baseTitle =
        options?.type === LogEventTypeEnum.USER_INTERACTION
          ? getUserInteractionTitle(panelTitleBase)
          : panelTitleBase;
      const modTitle = capitalizeInitialLetter(baseTitle);

      const resolvedSubCategory =
        options?.subCategory ??
        currentContext?.subCategory ??
        LogSubCategories.NONE;
      const resolvedCategory =
        options?.category ?? currentContext?.category ?? LogCategories.NONE;
      const logEvent: BaseLogEvent = {
        category: resolvedCategory,
        subCategory: resolvedSubCategory,
        tag: options?.tag ?? currentContext?.tag ?? LogTags.NONE,
        title: modTitle,
        type: options?.type ?? LogEventTypeEnum.USER_INTERACTION,
        context: {
          ...panelContext,
        },
        duration: options?.duration,
        mode:
          getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION)?.mode ||
          AuthMode.MEMBER,
        module: resolvedSubCategory || resolvedCategory || LogCategories.NONE,
      };

      logger.log(logEvent);
    },
    [buildBaseContext, logger],
  );

  const logPanelOpen = useCallback(
    (
      panelTitleBase: string,
      context: Record<string, unknown>,
      options?: PanelLogOptions,
    ) => {
      const base = !context?.panelName
        ? `${panelTitleBase.toLowerCase().split("-").join(" ")} opened`
        : `${(context?.panelName as string)?.toLowerCase()} opened`;
      const modTitle = capitalizeInitialLetter(base);
      return logPanelEvent(modTitle, { ...context }, options);
    },
    [logPanelEvent],
  );

  const logPanelClose = useCallback(
    (
      panelTitleBase: string,
      context: Record<string, unknown>,
      options?: PanelLogOptions,
    ) => {
      const base = !context?.panelName
        ? `${panelTitleBase.toLowerCase().split("-").join(" ")} closed`
        : `${(context?.panelName as string)?.toLowerCase()} closed`;
      const modTitle = capitalizeInitialLetter(base);
      return logPanelEvent(modTitle, { ...context }, options);
    },
    [logPanelEvent],
  );

  // const logPanelAction = useCallback(
  //    (panelTitleBase: string, context: Record<string, unknown>, options?: PanelLogOptions) =>
  //       logPanelEvent(panelTitleBase, "ACTION", context, options),
  //    [logPanelEvent],
  // );

  return { logPanelOpen, logPanelClose };
}

/**
 * Like useActionLogger but safe when used outside LoggerProvider: logAction is a no-op when no provider.
 * Use in components (e.g. Tabs) that may render with or without the logger tree.
 */
export function useActionLoggerOptional() {
  const context = useContext(LoggerContext);
  const location = useLocation();

  const logAction = useCallback(
    (
      actionName: string,
      logContext: Record<string, unknown>,
      options?: {
        debounceMs?: number;
        suppressDuplicate?: boolean;
        tag?: LogTags;
        category?: LogCategories;
        subCategory?: LogSubCategories;
        includeFormContext?: boolean;
        /** Use actionName as title without "x button clicked from y dialog" transform (e.g. dialog opened/closed) */
        useTitleAsIs?: boolean;
        type?: LogEventType;
        duration?: number;
      },
    ) => {
      if (!context?.logger) return;
      try {
        if (!actionName?.trim()) return;
        const routeMeta = getScreenMetadataFromPath(location.pathname);
        const activeContext = loggerContextManager.getActiveContext();
        const currentContext = routeMeta
          ? { ...routeMeta, ...activeContext }
          : activeContext;
        const lastLogKey = `lastLog_${actionName}`;
        const lastLogTime = lastActionLogAtByKey.get(lastLogKey);
        const debounceMs = options?.debounceMs ?? 0;
        const titleValue = actionName;
        if (lastLogTime && Date.now() - lastLogTime < debounceMs) {
          if (options?.suppressDuplicate) return;
        }
        lastActionLogAtByKey.set(lastLogKey, Date.now());
        const formContext = options?.includeFormContext
          ? loggerContextManager.getFormContext()
          : {};
        if (options?.includeFormContext) {
          loggerContextManager.clearFormContext();
        }

        const hasFormContext =
          options?.includeFormContext &&
          formContext &&
          Object.keys(formContext).length > 0;

        let eventContext: Record<string, unknown> = {
          ...logContext,
        };

        if (hasFormContext) {
          eventContext = { ...eventContext, ...formContext };
        }

        const resolvedSubCategory =
          options?.subCategory ??
          currentContext?.subCategory ??
          LogSubCategories.NONE;
        const resolvedCategory =
          options?.category ?? currentContext?.category ?? LogCategories.NONE;
        const logEvent: BaseLogEvent = {
          category: resolvedCategory,
          context: eventContext,
          tag: options?.tag ?? currentContext?.tag ?? LogTags.NONE,
          title: titleValue,
          subCategory: resolvedSubCategory,
          type: options?.type || LogEventTypeEnum.USER_INTERACTION,
          mode:
            getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION)?.mode ||
            AuthMode.MEMBER,
          module: resolvedSubCategory || resolvedCategory || LogCategories.NONE,
          ...(options?.duration !== undefined
            ? { duration: options.duration }
            : {}),
        };
        context.logger?.log(logEvent);
      } catch (error) {
        console.error(`Error logging user action ${actionName}:`, error);
      }
    },
    [context, location.pathname],
  );

  return { logAction };
}

/**
 * Optional hook to manually provide form context for action logging.
 * Primary path: form context is captured centrally when a submit Button is clicked (from the form element via FormData; sensitive keys excluded). No need to call this in each form.
 * Use this only when you need to override or supplement the auto-captured form context (e.g. non-native form libs that don't use name attributes).
 */
// export function useFormLogger() {
//    const setFormContext = useCallback(
//       (context: Record<string, unknown>) => {
//          loggerContextManager.setFormContext(context);
//       },
//       [],
//    );
//    const clearFormContext = useCallback(() => {
//       loggerContextManager.clearFormContext();
//    }, []);

//    return { setFormContext, clearFormContext };
// }

/**
 * Enhanced hook for error logging
 */
// export function useErrorLogger() {
//    const logger = useLogger();
//    const currentContext = loggerContextManager.getActiveContext();

//    const logError = useCallback((
//       error: Error,
//       errorContext?: Record<string, any>
//    ) => {
//       try {
//          const eventId = `error_${Date.now()}_${crypto.randomUUID()}`;

//          const logEvent: BaseLogEvent = {
//             category: "ERROR",
//             context: {
//                ...(currentContext || {}),
//                errorMessage: error.message,
//                errorStack: error.stack,
//                errorName: error.name,
//                eventId,
//                timestamp: new Date().toISOString(),
//                ...(errorContext || {}),
//             },
//             tag: currentContext?.tag || "UNKNOWN_ERROR",
//             title: `Error: ${error.message}`,
//             subCategory: currentContext?.subCategory || LogSubCategories.NONE,
//             type: "ERROR",
//          };

//          logger.log(logEvent);
//          console.error(`Error logged`, { eventId, error });
//       } catch (logError) {
//          console.error(`Failed to log error:`, logError);
//       }
//    }, [logger, currentContext]);

//    return { logError };
// }

export function useSendLogsMutation() {
  return useMutation({
    mutationFn: ({
      logs,
      instanceId,
    }: {
      logs: BaseLogEvent[];
      instanceId?: string;
    }) => {
      return sendLogs(logs, instanceId);
    },
    onError: (error) => {
      console.error("Failed to send logs", error);
    },
  });
}
